/*!
 * BRAND GUARD v2 — anti-mirror / anti-reverse-proxy guard for grizas
 * Built by BBOLD.online, 2026-09. Replaces the v1 snippet (Productive task #81).
 *
 * WHY v1 FAILED
 * -------------
 * The mirrors (grizas.live, grizas.pro, grizas.info) are text-substitution
 * reverse proxies. They fetch the real page and replace every occurrence of
 * the canonical host string in the response body with their own host. v1
 * carried that host as a plain string literal, so the proxy rewrote v1's own
 * allowlist and its redirect target. The check then passed on the clone.
 *
 * THE RULE THAT MAKES v2 WORK
 * ---------------------------
 * The canonical host must NEVER appear as a contiguous literal in anything we
 * ship. Below it is assembled from character codes. A blind find-and-replace
 * has nothing to match, and the check runs on location.hostname, which the
 * proxy cannot forge in the visitor's browser.
 *
 * DO NOT "clean up" the String.fromCharCode() lines into plain strings, and do
 * not run this file through a minifier that folds them. That would re-open the
 * exact hole v1 had. The one plain literal (CANARY) is deliberate: it is bait.
 */
(function () {
  "use strict";

  /* ---------------------------------------------------------------- config */

  // 'redirect' = send mirrored visitors to the real store (production)
  // 'warn'     = show the overlay only, do not navigate
  // 'report'   = do nothing visible, just report to the beacon (use this for
  //              the first 24h after deploy to prove there are no false hits)
  var MODE = "redirect";

  // Layer 3 host. MUST be set before deploy — see "Choosing the guard host"
  // in docs/01-DEPLOY-AND-TEST.md. Two hard requirements:
  //   1. it contains no part of the client's domain, so the mirror has no
  //      reason to rewrite it. A subdomain of the store's own domain would be
  //      rewritten to the mirror's, the request would 404, and this whole
  //      layer would die silently;
  //   2. its zone is on Cloudflare, in our own account.
  // Layers 1 and 2 work fine while this is unset; only the alarm is lost.
  var BEACON = "https://brand-guard.sales-dba.workers.dev/px/b.gif";

  // Optional: stop mirrored traffic from polluting GA4. Put the real
  // measurement ID here, or leave null. Only effective in the inline head
  // copy of this guard, which runs before the GA snippet.
  var GA4_ID = "G-N4W4LXMG0J"; // e.g. "G-XXXXXXXXXX"

  /* -------------------------------------------------- identity (code-built) */

  var cc = String.fromCharCode;
  var B = cc(103, 114, 105, 122, 97, 115); // brand label
  var T = cc(99, 111, 109);                // primary TLD

  var CANON = B + "." + T;                 // canonical storefront host
  var ALT = [B + ".de", B + ".fr"];        // other live storefront hosts
  var SHOP = B + "-" + T + ".myshopify.com"; // Shopify permanent domain

  // Hosts we must never act on. None of these contain the canonical host as a
  // substring, so they are safe to keep as plain literals: if a proxy ever did
  // rewrite them the check simply fails closed.
  var SAFE_SUFFIX = [
    ".myshopify.com",
    ".shopify.com",          // admin.shopify.com, cdn.shopify.com
    ".shopifypreview.com",   // theme previews / share links
    ".translate.goog"        // Google Translate proxy — a real user, leave alone
  ];
  var SAFE_EXACT = ["localhost", "127.0.0.1"];

  /* ------------------------------------------------------------- primitives */

  function allowed(h) {
    if (!h) return true; // no hostname to judge — never act blind
    h = String(h).toLowerCase();
    if (h === CANON || h === "www." + CANON) return true;
    for (var i = 0; i < ALT.length; i++) {
      if (h === ALT[i] || h === "www." + ALT[i]) return true;
    }
    for (var j = 0; j < SAFE_SUFFIX.length; j++) {
      var s = SAFE_SUFFIX[j];
      if (h.length > s.length && h.slice(-s.length) === s) return true;
    }
    for (var k = 0; k < SAFE_EXACT.length; k++) {
      if (h === SAFE_EXACT[k]) return true;
    }
    return false;
  }

  function isHome(h) {
    return h === CANON || h === "www." + CANON;
  }

  function report(host, reason) {
    var q = "?h=" + encodeURIComponent(host) +
            "&r=" + encodeURIComponent(reason) +
            "&p=" + encodeURIComponent(location.pathname) +
            "&t=" + Date.now();
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(BEACON + q)) return;
    } catch (e) { /* fall through */ }
    try { new Image().src = BEACON + q; } catch (e) { /* nothing else to try */ }
  }

  function realUrl(host) {
    var sep = location.search ? "&" : "?";
    return "https://" + CANON + location.pathname +
           location.search + sep + "cg=" + encodeURIComponent(host) +
           location.hash;
  }

  function noindex() {
    // Mirrors rank on our content. Crawlers that execute JS will read this and
    // drop the clone's pages, which the redirect alone does not guarantee.
    try {
      var m = document.createElement("meta");
      m.name = "robots";
      m.content = "noindex, nofollow";
      (document.head || document.documentElement).appendChild(m);
    } catch (e) { /* head not ready; the redirect is the primary defence */ }
  }

  function overlay(url) {
    try {
      if (document.getElementById("bg-notice")) return;
      var d = document.createElement("div");
      d.id = "bg-notice";
      d.setAttribute("style", [
        "position:fixed", "inset:0", "z-index:2147483647",
        "background:#111", "color:#fff", "display:flex",
        "flex-direction:column", "align-items:center",
        "justify-content:center", "gap:18px", "padding:32px",
        "font:16px/1.5 system-ui,-apple-system,Segoe UI,Roboto,sans-serif",
        "text-align:center"
      ].join(";"));
      var t = document.createElement("p");
      t.setAttribute("style", "max-width:34em;margin:0");
      t.textContent = "This website is an unauthorised copy of our store. " +
        "Do not enter payment or personal details here.";
      var a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      a.textContent = "Go to the official store";
      a.setAttribute("style",
        "background:#fff;color:#111;padding:12px 22px;border-radius:2px;" +
        "text-decoration:none;font-weight:600;letter-spacing:.02em");
      d.appendChild(t);
      d.appendChild(a);
      (document.body || document.documentElement).appendChild(d);
      try { document.documentElement.style.overflow = "hidden"; } catch (e) {}
    } catch (e) { /* if we cannot paint, the redirect already ran */ }
  }

  function leave(host) {
    var url = realUrl(host);
    noindex();
    if (MODE === "warn") {
      if (document.body) overlay(url);
      else document.addEventListener("DOMContentLoaded", function () { overlay(url); });
      return;
    }
    try {
      if (window.top !== window.self) { window.top.location.replace(url); return; }
    } catch (e) { /* cross-origin top; fall through to self-navigation */ }
    try { location.replace(url); } catch (e) { location.href = url; }
    // If navigation is blocked or slow, make sure nothing usable is on screen.
    setTimeout(function () { overlay(url); }, 1500);
  }

  /* ------------------------------------------------------------------- run */

  // Never interfere with the Shopify theme editor.
  try { if (window.Shopify && window.Shopify.designMode) return; } catch (e) {}

  var host = (location.hostname || "").toLowerCase();
  var ok = allowed(host);

  // Bait. This literal is the only place the canonical host is spelled out, so
  // a rewriting proxy replaces it and it stops matching the code-built value.
  // It proves the response body was tampered with, even on a host we allow.
  var CANARY = "grizas.com";
  var tampered = CANARY !== CANON;

  // Second canary: Shopify prints the shop's permanent domain into the page.
  // On the mirrors it came through as "grizas.live.myshopify.com" — a host that
  // cannot exist. Only meaningful once Shopify's own script has run.
  try {
    if (window.Shopify && window.Shopify.shop &&
        String(window.Shopify.shop).toLowerCase() !== SHOP) {
      tampered = true;
    }
  } catch (e) {}

  if (!ok) {
    if (GA4_ID) { try { window["ga-disable-" + GA4_ID] = true; } catch (e) {} }
    report(host, tampered ? "host+rewrite" : "host");
    if (window.__BG_ACTED__) return;
    window.__BG_ACTED__ = 1;
    if (MODE !== "report") leave(host);
    return;
  }

  // Allowed host but the body was altered: do NOT navigate (that could loop or
  // bounce legitimate .de/.fr shoppers). Tell us instead — this is how we find
  // out that someone has started rewriting our pages a new way.
  if (tampered) { report(host, "rewrite"); }

  // Frame-busting: a mirror can also wrap the real store in an iframe and put
  // its own checkout chrome around it.
  try {
    if (window.top !== window.self) {
      var parentHost = "";
      var ao = location.ancestorOrigins;
      if (ao && ao.length) {
        parentHost = new URL(ao[ao.length - 1]).hostname.toLowerCase();
      } else if (document.referrer) {
        parentHost = new URL(document.referrer).hostname.toLowerCase();
      }
      if (parentHost && !allowed(parentHost) && !isHome(parentHost)) {
        report(parentHost, "frame");
        if (MODE !== "report") window.top.location.replace(location.href);
      }
    }
  } catch (e) {
    // Access to the parent threw: we are framed by a foreign origin.
    try {
      report("(opaque-parent)", "frame");
      if (MODE !== "report") window.top.location.replace(location.href);
    } catch (e2) {}
  }
})();
